import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Play, Pause, Heart, Download, ExternalLink, Loader2 } from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import { useSEO } from '../../utils/seo';
import './PromoLandingPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  spotify: { label: 'Spotify', color: '#1DB954' },
  apple_music: { label: 'Apple Music', color: '#FA243C' },
  soundcloud: { label: 'SoundCloud', color: '#FF5500' },
  beatport: { label: 'Beatport', color: '#94D500' },
  youtube: { label: 'YouTube', color: '#FF0000' },
  deezer: { label: 'Deezer', color: '#A238FF' },
};

export default function PromoLandingPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const refToken = searchParams.get('ref');
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [released, setReleased] = useState(null); // { slug, title }
  const [isPlaying, setIsPlaying] = useState(false);
  const [liked, setLiked] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasPlayed, setHasPlayed] = useState(false);
  const audioRef = useRef(null);

  useSEO({ title: promo ? `${promo.title} — ${promo.artist_name}` : 'Promo', description: promo?.description });

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPromo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promo/${slug}`);
        if (res.status === 410) {
          setExpired(true);
          return;
        }
        if (res.status === 301) {
          const releaseSlug = res.headers.get('X-Release-Slug');
          const releaseTitle = res.headers.get('X-Release-Title');
          setReleased({ slug: releaseSlug, title: releaseTitle || 'this release' });
          return;
        }
        if (!res.ok) throw new Error(res.status === 404 ? 'This promo page does not exist or has not been published.' : 'Failed to load');
        setPromo(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPromo();
  }, [slug]);

  const fireEvent = useCallback(async (eventType) => {
    // Internal AMR tracking
    try {
      await fetch(`${API_URL}/api/promo/${slug}/event?event_type=${eventType}`, { method: 'POST' });
    } catch { /* silent */ }

    // Heyday / external campaign tracking (fires when ?ref= token is in URL)
    if (refToken && promo?.tracking_url) {
      try {
        const baseUrl = promo.tracking_url.replace(/\/+$/, '');
        await fetch(`${baseUrl}/api/promo/event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: refToken, event: eventType }),
        });
      } catch { /* silent */ }
    }
  }, [slug, refToken, promo?.tracking_url]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
      if (!hasPlayed) {
        setHasPlayed(true);
        fireEvent('play');
      }
    }
    setIsPlaying(!isPlaying);
  };

  const handleLike = () => {
    if (liked) return;
    setLiked(true);
    fireEvent('like');
  };

  const handleDownload = () => {
    fireEvent('download');
    window.open(`${API_URL}/api/promo/${slug}/download`, '_blank');
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setCurrentTime(audio.currentTime);
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * audio.duration;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (loading) return (
    <div className="promo-page">
      <div className="promo-page__loader"><Loader2 size={32} className="promo-page__spin" /></div>
    </div>
  );

  if (expired) return (
    <div className="promo-page">
      <div className="promo-page__error" data-testid="promo-expired">
        <img src="/amr-logo.png" alt="AMR" className="promo-page__logo promo-page__logo--lg" />
        <h2>Promo Expired</h2>
        <p>This promotional page is no longer available.</p>
        <a href="https://anyonemusicrecords.com" className="promo-page__cta-home">VISIT AMR HOMEPAGE</a>
      </div>
    </div>
  );

  if (released) return (
    <div className="promo-page">
      <div className="promo-page__error" data-testid="promo-released">
        <img src="/amr-logo.png" alt="AMR" className="promo-page__logo promo-page__logo--lg" />
        <h2>Now Available!</h2>
        <p>This release is now available on all platforms.</p>
        {released.slug && (
          <a href={`/releases/${released.slug}`} className="promo-page__cta-home" style={{ marginBottom: 12 }}>
            VIEW RELEASE
          </a>
        )}
        <a href="https://anyonemusicrecords.com" className="promo-page__cta-home" style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
          VISIT AMR HOMEPAGE
        </a>
      </div>
    </div>
  );

  if (error) return (
    <div className="promo-page">
      <div className="promo-page__error" data-testid="promo-error">
        <img src="/amr-logo.png" alt="AMR" className="promo-page__logo promo-page__logo--lg" />
        <h2>Not Available</h2>
        <p>{error}</p>
        <a href="https://anyonemusicrecords.com" className="promo-page__cta-home">VISIT AMR HOMEPAGE</a>
      </div>
    </div>
  );

  const coverUrl = promo.cover_art ? getImageUrl(promo.cover_art) : null;
  const audioSrc = promo.mp3_url ? `${API_URL}/api/promo/${slug}/audio` : null;
  const links = promo.streaming_links || {};
  const hasLinks = Object.values(links).some(v => v);
  const beatportLink = links.beatport;
  const artistSlug = promo.artist?.slug;

  return (
    <div className="promo-page" data-testid="promo-landing-page">
      {coverUrl && <div className="promo-page__bg" style={{ backgroundImage: `url(${coverUrl})` }} />}

      <div className="promo-page__container">
        {/* Header */}
        <header className="promo-page__header">
          <img src="/amr-logo.png" alt="Anyone Music Records" className="promo-page__logo promo-page__logo--lg" />
          <span className="promo-page__label-amr">ANYONE MUSIC RECORDS</span>
        </header>

        {/* Hero: 2-column */}
        <section className="promo-page__hero" data-testid="promo-hero">
          <div className="promo-page__hero-art" data-testid="promo-artwork">
            {coverUrl ? (
              <img src={coverUrl} alt={promo.title} />
            ) : (
              <div className="promo-page__art-placeholder"><Play size={48} /></div>
            )}
          </div>

          <div className="promo-page__hero-info" data-testid="promo-info">
            {promo.is_pre_release && <span className="promo-page__badge" data-testid="promo-prerelease-badge">Pre Release</span>}

            <h1 className="promo-page__title">{promo.title}</h1>

            {artistSlug ? (
              <a href={`/artists/${artistSlug}`} className="promo-page__artist-link" data-testid="promo-artist-link">
                {promo.artist_name}
              </a>
            ) : (
              <p className="promo-page__artist">{promo.artist_name}</p>
            )}

            {(promo.genre || promo.sub_genre) && (
              <p className="promo-page__genre" data-testid="promo-genre">
                {promo.genre}{promo.sub_genre ? ` / ${promo.sub_genre}` : ''}
              </p>
            )}

            {promo.release_date && (
              <p className="promo-page__avail" data-testid="promo-release-date">
                {promo.is_pre_release ? 'Available to purchase on: ' : 'Available from: '}
                <strong>{formatDate(promo.release_date)}</strong>
              </p>
            )}

            {promo.description && (
              <div className="promo-page__desc" data-testid="promo-description">
                {promo.description.split('\n').map((line, i) => <p key={i}>{line}</p>)}
              </div>
            )}
          </div>
        </section>

        {/* Listen Now + Player */}
        {audioSrc && (
          <section className="promo-page__listen" data-testid="promo-audio-section">
            <div className="promo-page__listen-label">LISTEN NOW</div>
            <div className="promo-page__player" data-testid="promo-audio-player">
              <audio
                ref={audioRef}
                src={audioSrc}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
                onEnded={() => setIsPlaying(false)}
                preload="metadata"
              />
              <button className="promo-page__play-btn" onClick={togglePlay} data-testid="promo-play-btn">
                {isPlaying ? <Pause size={22} /> : <Play size={22} />}
              </button>
              <div className="promo-page__track-label">
                <span className="promo-page__track-title">{promo.title}</span>
                <span className="promo-page__track-artist">{promo.artist_name}</span>
              </div>
              <div className="promo-page__progress-wrap">
                <div className="promo-page__progress-bar" onClick={handleSeek} data-testid="promo-progress-bar">
                  <div className="promo-page__progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="promo-page__times">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Like + Download Section */}
        <section className="promo-page__download-section" data-testid="promo-download-section">
          {!liked ? (
            <button className="promo-page__like-btn" onClick={handleLike} data-testid="promo-like-btn">
              <Heart size={20} />
              Like this track to download in WAV format
            </button>
          ) : (
            <div className="promo-page__liked-area">
              <div className="promo-page__liked-msg">
                <Heart size={18} fill="currentColor" /> Thanks for the love!
              </div>
              {promo.has_wav && (
                <button className="promo-page__download-btn" onClick={handleDownload} data-testid="promo-download-btn">
                  <Download size={20} />
                  Download WAV
                </button>
              )}
            </div>
          )}
        </section>

        {/* Beatport Link */}
        {beatportLink && (
          <div className="promo-page__beatport" data-testid="promo-beatport">
            <a href={beatportLink} target="_blank" rel="noreferrer" className="promo-page__beatport-link">
              <ExternalLink size={16} />
              Available for download on Beatport
            </a>
          </div>
        )}

        {/* Streaming Links */}
        {hasLinks && (
          <div className="promo-page__links" data-testid="promo-streaming-links">
            <p className="promo-page__links-label">Listen on</p>
            <div className="promo-page__links-grid">
              {Object.entries(links).map(([key, url]) => {
                if (!url || key === 'beatport') return null;
                const info = PLATFORM_ICONS[key] || { label: key, color: '#888' };
                return (
                  <a key={key} href={url} target="_blank" rel="noreferrer"
                    className="promo-page__link-pill" style={{ '--pill-color': info.color }}
                    data-testid={`promo-link-${key}`}>
                    <ExternalLink size={14} />
                    {info.label}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <a href="https://anyonemusicrecords.com" className="promo-page__cta-home" data-testid="promo-visit-amr">
          VISIT AMR HOMEPAGE
        </a>

        <footer className="promo-page__footer">
          <p>&copy; {new Date().getFullYear()} Anyone Music Records</p>
        </footer>
      </div>
    </div>
  );
}
